import { useEffect } from 'react';

import {
  AvailabilityState,
  GetCatalogProductViewQuery,
} from '@notino/shared/definitions/types';

import { EXCLUDE_FROM_LAST_VISITED } from '@utils/constants';
import { requestIdleCallback } from '@utils/requestIdleCallback';

import { appendProductToCookie, appendVariantToCookie } from './utils';

type Product = NonNullable<
  GetCatalogProductViewQuery['productDetailByCatalogMasterId']
>;

type IUseAddToLastVisited = Pick<Product, 'variants' | 'webMasterId'>;

export const useAddToLastVisited = (
  { webMasterId, variants }: IUseAddToLastVisited,
  currentVariant: Product['variants'][number]
) => {
  const currentVariantCatalogId = currentVariant.catalogId;
  const currentVariantState = currentVariant.availability.state;

  useEffect(() => {
    requestIdleCallback(() => {
      const canSaveToLastVisited = variants.every(
        (variant) =>
          !variant.navigationValues.includes(EXCLUDE_FROM_LAST_VISITED)
      );

      if (!canSaveToLastVisited) {
        return;
      }

      // Legacy cookie (web master IDs) consumed by the homepage carousel.
      // Keep the original behaviour untouched for backwards compatibility.
      const canBuy = variants.some(
        (variant) =>
          variant.availability.state === AvailabilityState.CanBeBought
      );
      if (canBuy) {
        appendProductToCookie(webMasterId);
      }

      // New cookie: store the viewed variant when it is available or sold out
      // (ShowWatchdog) but not when its sale has ended (Disabled), so the PD
      // carousel can show the exact viewed variant instead of the master.
      const isViewedVariantVisible =
        currentVariantState === AvailabilityState.CanBeBought ||
        currentVariantState === AvailabilityState.ShowWatchdog;
      if (isViewedVariantVisible) {
        appendVariantToCookie(
          String(currentVariantCatalogId),
          variants.map((variant) => String(variant.catalogId))
        );
      }
    });
  }, [webMasterId, variants, currentVariantCatalogId, currentVariantState]);
};
